<script setup lang="ts">
import { useMainNewsList, useMainNewsSelected } from '~/shared/lib/useMainNews';
import { formatDate } from '~/shared/utils/formatDate';
import type { IMainNewsDataClientDTO } from '~/shared/api/client/main-news/main-news.dto';
import {
  CLIENT_MAIN_NEWS_URL,
  CLIENT_MAIN_NEWS_KEY,
} from '~/shared/api/client/publisher/publisher.const';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { getMaterialPublicId } from '~/shared/lib/getMaterialLink';
import { resizeCropSlot } from '~/shared/lib/resizeImage';
import { CROP_RATIO_MAIN_NEWS } from '~/shared/lib/crop-ratio.const';
import {
  MAIN_NEWS_IMAGE_DESKTOP_WIDTH,
  MAIN_NEWS_IMAGE_MOBILE_WIDTH,
} from '../lib/const';

const { $viewport } = useNuxtApp();

const isMobile = computed(() => $viewport.isLessOrEquals('tabletBig'));

const isTabletBig = computed(() => $viewport.match('tabletBig'));

const newsList = useMainNewsList();
const newsListSelected = useMainNewsSelected();

const { data, status } = await useCustomFetch<IMainNewsDataClientDTO[]>(
  CLIENT_MAIN_NEWS_URL,
  {
    method: 'GET',
    key: CLIENT_MAIN_NEWS_KEY,
  },
);

if (data.value && data.value.length > 0) {
  newsList.value = data.value.map((item) => item.publisher_news[0]!);
  if (!newsListSelected.value) {
    newsListSelected.value = data.value[0]!.publisher_news[0]!;
  }
}

const item = computed(() => newsListSelected.value);
const isExpert = computed(
  () => item.value?.expert !== null && item.value?.expert !== undefined,
);

const isRedirect = computed(() =>
  Boolean(item.value?.news?.redirect && item.value?.news.redirect.length > 0),
);

const link = computed(() => {
  if (isRedirect.value && item.value?.news?.redirect)
    return item.value.news.redirect;
  if (isExpert.value)
    return `/expert/${getMaterialPublicId(item.value!.expert!)}`;
  return `/${item.value?.news?.type}/${item.value?.news?.id}`;
});
const categoryLink = computed(() =>
  isExpert.value ? '/expert' : `/category/${item.value?.news?.category?.id}`,
);
const categoryName = computed(() =>
  isExpert.value ? 'Мнения экспертов' : item.value?.news?.category?.name,
);

const imageOne = computed(
  () =>
    item.value?.image_one?.file_url ||
    item.value?.news?.image?.file_url ||
    item.value?.expert?.image?.file_url ||
    undefined,
);
const imageTwo = computed(
  () =>
    item.value?.image_two?.file_url ||
    item.value?.news?.image?.file_url ||
    item.value?.expert?.image?.file_url ||
    undefined,
);

const imageOneAttrs = computed(() =>
  resizeCropSlot(imageOne.value, CROP_RATIO_MAIN_NEWS, 0, {
    width: MAIN_NEWS_IMAGE_DESKTOP_WIDTH,
  }),
);

const imageTwoAttrs = computed(() =>
  resizeCropSlot(imageTwo.value, CROP_RATIO_MAIN_NEWS, 1, {
    width: MAIN_NEWS_IMAGE_MOBILE_WIDTH,
  }),
);

const itemRef = ref<HTMLElement | null>(null);

const infoRef = ref<HTMLElement | null>(null);
const infoInnerRef = ref<HTMLElement | null>(null);
const categoryRef = ref<HTMLElement | null>(null);
const titleRef = ref<ComponentPublicInstance | null>(null);
const textRef = ref<HTMLElement | null>(null);
const pictureRef = ref<ComponentPublicInstance | null>(null);

// базовые лимиты из дизайна (и SSR-дефолт)
const TITLE_LINES = 4;
const TEXT_LINES = 3;

const titleClamp = ref(TITLE_LINES);
const textClamp = ref(TEXT_LINES);

// NuxtLink — компонент, DOM-узел лежит в $el
const getEl = (r: ComponentPublicInstance | null) =>
  (r?.$el as HTMLElement | undefined) ?? null;

/*
Адаптивные line-clamp заголовка и подзаголовка (row-лейаут, >=768px).
Базово: заголовок до 4 строк, подзаголовок до 3 — как в дизайне. Если после
этого до низа контейнера остаётся место, его забирает сначала заголовок
(может стать 5+ строк при коротком подзаголовке), затем подзаголовок.
Бюджет высоты — max(высота картинки, min-height контейнера минус паддинги):
это CSS-заявленные величины, не зависящие от текущих клампов (мерить
отрендеренную высоту контейнера нельзя — она сама растёт от наших клампов,
получается петля). scrollHeight включает скрытое клампом — тоже без петли.
*/
const updateClamps = () => {
  const titleEl = getEl(titleRef.value);
  const textEl = textRef.value;
  if (!titleEl || !textEl) return;

  if (!$viewport.isGreaterOrEquals('tablet')) {
    titleClamp.value = TITLE_LINES;
    textClamp.value = TEXT_LINES;
    return;
  }

  const titleLh = parseFloat(getComputedStyle(titleEl).lineHeight);
  const textLh = parseFloat(getComputedStyle(textEl).lineHeight);
  if (!titleLh || !textLh) return;

  // естественное число строк, без учёта клампа
  const titleNatural = Math.round(titleEl.scrollHeight / titleLh);
  const textNatural = Math.round(textEl.scrollHeight / textLh);

  let title = Math.min(titleNatural, TITLE_LINES);
  let text = Math.min(textNatural, TEXT_LINES);

  const itemEl = itemRef.value;
  const info = infoRef.value;
  const inner = infoInnerRef.value;
  const budget = (() => {
    if (!itemEl) return 0;
    const itemStyle = getComputedStyle(itemEl);
    const minHeight = parseFloat(itemStyle.minHeight) || 0;
    const verticalExtras =
      (parseFloat(itemStyle.paddingTop) || 0) +
      (parseFloat(itemStyle.paddingBottom) || 0) +
      (parseFloat(itemStyle.borderBottomWidth) || 0);
    // картинка тянет контейнер на свою высоту + margin-bottom
    const pictureEl = getEl(pictureRef.value);
    const pictureH = pictureEl
      ? pictureEl.offsetHeight +
        (parseFloat(getComputedStyle(pictureEl).marginBottom) || 0)
      : 0;
    return Math.max(pictureH, minHeight - verticalExtras);
  })();

  if (budget > 0 && info && inner) {
    const infoGap = parseFloat(getComputedStyle(info).rowGap) || 0;
    const innerGap = parseFloat(getComputedStyle(inner).rowGap) || 0;
    const categoryH = categoryRef.value
      ? categoryRef.value.offsetHeight + innerGap
      : 0;

    // свободное место до низа контейнера; +0.5px — допуск на округление
    let leftover =
      budget - categoryH - infoGap - title * titleLh - text * textLh + 0.5;

    const titleGrow = Math.min(
      titleNatural - title,
      Math.max(0, Math.floor(leftover / titleLh)),
    );
    title += titleGrow;
    leftover -= titleGrow * titleLh;

    text += Math.min(
      textNatural - text,
      Math.max(0, Math.floor(leftover / textLh)),
    );
  }

  titleClamp.value = Math.max(1, title);
  textClamp.value = Math.max(1, text);
};

let clampResizeObserver: ResizeObserver | null = null;

onMounted(() => {
  if (!infoRef.value) return;
  // ресайз окна / смена брейкпоинта / подгрузка шрифтов
  clampResizeObserver = new ResizeObserver(updateClamps);
  clampResizeObserver.observe(infoRef.value);
});

onUnmounted(() => {
  clampResizeObserver?.disconnect();
});

watch(newsListSelected, (newVal) => {
  // другой контент — другие натуральные высоты, пересчитываем клампы
  nextTick(updateClamps);

  if (newVal && itemRef.value && isMobile.value) {
    const headerOffset = isTabletBig.value ? 64 : 0; // высота шапки
    const elementRect = itemRef.value.getBoundingClientRect();
    const offsetPosition = elementRect.top + window.scrollY - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
});
</script>

<template>
  <template v-if="status === 'success' && data && data.length > 0">
    <div ref="itemRef" :class="$style.item">
      <div ref="infoRef" :class="$style.item__info">
        <div ref="infoInnerRef" :class="$style.item__infoInner">
          <div
            v-if="categoryName"
            ref="categoryRef"
            :class="$style.item__category"
          >
            <NuxtLink :to="categoryLink" :class="$style.item__categoryLink">
              {{ categoryName }}</NuxtLink
            ><span v-if="item?.news?.publication_date">, </span>
            <span v-if="item?.news?.publication_date">
              {{ formatDate('shortDate', item?.news?.publication_date) }}
            </span>
          </div>

          <NuxtLink
            ref="titleRef"
            :to="link"
            :class="$style.item__title"
            :style="{ '--title-clamp': titleClamp }"
            :target="isRedirect ? '_blank' : undefined"
          >
            {{ newsListSelected?.title }}
          </NuxtLink>
        </div>
        <div
          ref="textRef"
          :class="$style.item__text"
          :style="{ '--text-clamp': textClamp }"
          v-html="newsListSelected?.subject"
        />
        <!-- <ViewsCounter
          v-if="newsListSelected?.news.views"
          :class="$style.item__views"
          :views="newsListSelected?.news.views"
        /> -->
      </div>
      <NuxtLink
        v-if="imageOne || imageTwo"
        ref="pictureRef"
        :to="link"
        :class="$style.item__pictureWrapper"
        :target="isRedirect ? '_blank' : undefined"
      >
        <picture :class="$style.item__picture">
          <source
            v-if="imageTwo"
            :srcset="imageTwoAttrs.srcset || imageTwoAttrs.src"
            media="(max-width: 767px)"
          />
          <img
            v-if="imageOne"
            :src="imageOneAttrs.src"
            :srcset="imageOneAttrs.srcset || undefined"
            :alt="newsListSelected?.title"
            :class="$style.item__pictureImg"
          />
        </picture>
      </NuxtLink>
    </div>
  </template>
  <div
    v-else-if="
      status === 'error' || (status === 'success' && data && data.length === 0)
    "
    :class="$style.error"
  >
    <p>Не удалось получить блок Главное</p>
    <p>Попробуйте еще раз</p>
  </div>
  <template v-else-if="status === 'pending'">
    <Loader />
  </template>
</template>

<style module lang="scss">
.item {
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
  border-bottom: 1px solid var(--color-gray-line);
  padding: var(--space-2-5x);

  &__info {
    position: relative;
    display: flex;
    flex-flow: column nowrap;
    gap: var(--space-2x);
    &Inner {
      display: flex;
      flex-flow: column nowrap;
      gap: var(--space-1x);
    }
  }

  &__category {
    @include text_caption1_caps;
    @include line_clamp(1);
    color: var(--color-gray-text);

    &Link {
      color: var(--color-gray-text);
      text-decoration: none;
    }
  }

  &__title {
    color: var(--color-black-1);
    text-decoration: none;
    word-break: break-word;
    @include line_clamp(var(--title-clamp, 4));
    flex-shrink: 0;
    margin-bottom: -4px;
    padding-bottom: 4px; // правка, чтобы нижние символы помещались полностью, в случае с буквами g, y
  }

  &__text {
    @include text_body1_large_medium;
    word-break: break-word;
    @include line_clamp(var(--text-clamp, 3));
    color: var(--color-black-1);
  }

  &__picture {
    &Img {
      width: 100%;
      height: 280px;
      object-fit: cover;
    }

    &Wrapper {
      width: 500px;
      height: 280px;
      flex-shrink: 0;
      margin-bottom: 3px;
    }
  }

  &__views {
    position: absolute;
    bottom: 0;
  }
}

@media (hover: hover) {
  .item {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (min-width: 1380px) {
  .item {
    gap: var(--space-2x);

    &__category {
      @include text_caption1_caps;
    }

    &__title {
      @include text_h2;
    }
  }
}

@media (max-width: 1379px) {
  .item {
    gap: var(--space-2-5x);
    padding-bottom: var(--space-2-5x);

    &__info {
      gap: var(--space-1x);
    }

    &__text {
      @include text_body1_bold;
    }
  }
}

@media (max-width: 1379px) and (hover: hover) {
  .item {
    min-height: 264px;
  }
}

@media (min-width: 768px) and (max-width: 1379px) {
  .item {
    gap: var(--space-1x);

    &__title {
      @include text_h4;
      font-size: 28px;
    }

    &__picture {
      &Img {
        height: 197px;
      }

      &Wrapper {
        width: 352px;
        height: 197px;
      }
    }
  }
}

@media (max-width: 767px) {
  .item {
    height: inherit;
    &__views {
      position: static;
    }
  }
}

@media (min-width: 480px) and (max-width: 767px) {
  .item {
    &__title {
      @include text_h4;
    }
  }
}

@media (min-width: 600px) and (max-width: 767px) {
  .item {
    flex-direction: column-reverse;

    &__picture {
      &Img {
        height: 246px;
      }

      &Wrapper {
        width: 560px;
        height: 246px;
      }
    }
  }
}

@media (min-width: 480px) and (max-width: 599px) {
  .item {
    flex-direction: column-reverse;

    &__picture {
      background-size: cover;

      &Img {
        height: 246px;
      }

      &Wrapper {
        width: 100%;
        height: 246px;
      }
    }
  }
}

@media (max-width: 479px) {
  .item {
    flex-direction: column-reverse;
    gap: var(--space-1-5x);
    padding: var(--space-2x) 0;

    &__info {
      padding-left: var(--space-2x);
      padding-right: var(--space-2x);
      position: relative;

      &Inner {
        position: absolute;
        top: -12px; // GAP
        transform: translateY(-100%);
        left: 0;
        justify-content: end;
        height: 192px;
        padding-bottom: var(--space-2x);
        z-index: 1;
      }
    }

    &__category {
      padding-left: var(--space-2x);
      padding-right: var(--space-2x);
      color: var(--color-white);
      z-index: 1;

      &Link {
        color: inherit;
      }
    }

    &__title {
      @include text_h5;
      @include line_clamp(4);
      padding-left: var(--space-2x);
      padding-right: var(--space-2x);
      color: var(--color-white);
    }

    &__text {
      @include text_body2_bold;
    }

    &__picture {
      background-size: cover;

      &Img {
        height: 192px;
      }

      &Wrapper {
        width: 100%;
        height: 192px;
        position: relative;
        margin-bottom: 0;

        &::before {
          position: absolute;
          content: '';
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            180deg,
            #111111 19.81%,
            rgba(40, 40, 40, 0.2) 50.44%,
            rgba(93, 93, 93, 0.2) 75.66%,
            #5e5e5e 100%
          );
          opacity: 0.7;
          transform: matrix(1, 0, 0, -1, 0, 0);
        }
      }
    }
  }
}

.error {
  text-align: center;
  padding-top: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--color-gray-line);
  p {
    margin: 10px 0;
  }
}
</style>
